/**
 * Public thin intake demo — IF-citizen-thin-intake-v1 Form→Flow→App golden.
 */
import goldenIntakeApp from '../../../packages/builder-schema/fixtures/golden-exports/IF-citizen-thin-intake-v1.app.json';
import { getDiscoverySessionId } from '../utils/discoverySession.js';
import { intakeDefaultsFromSession } from '../utils/discoverySessionHydrate.js';

export const INTAKE_PUBLIC_DEMO_ID = 'if-citizen-thin-intake-demo';
export const INTAKE_GOLDEN_APP_NAME = 'IF-citizen-thin-intake-v1';

function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * @param {{
 *   jurisdictionId?: string,
 *   packageId?: string,
 *   sessionId?: string,
 *   applicationTypeId?: string,
 *   defaults?: object,
 *   session?: object|null,
 * }} [opts]
 */
export function buildIntakePublicDemoDefinition(opts = {}) {
  const jurisdictionId = opts.jurisdictionId || opts.packageId || 'san-francisco';
  const sessionId = opts.sessionId || opts.session?.sessionId || getDiscoverySessionId() || '';
  const applicationTypeId = opts.applicationTypeId
    || opts.session?.applicationTypeId
    || 'building_permit';
  const fromSession = intakeDefaultsFromSession(opts.session || null, {
    sessionId,
    packageId: jurisdictionId,
    applicationTypeId,
  });
  const defaults = {
    ...fromSession,
    ...(opts.defaults || {}),
    sessionId: sessionId || fromSession.sessionId || '',
    packageId: jurisdictionId || 'san-francisco',
    applicationTypeId: applicationTypeId || fromSession.applicationTypeId,
    createDraftWhenReady: opts.defaults?.createDraftWhenReady
      || fromSession.createDraftWhenReady
      || 'yes',
  };
  const def = clone(goldenIntakeApp);
  def.id = INTAKE_PUBLIC_DEMO_ID;
  def.name = def.name || INTAKE_GOLDEN_APP_NAME;
  def.metadata = def.metadata || {};
  // Track skill from session schema / catalog; SoR type detail via requirements tool.
  const track = opts.session?.intakeSchema?.track
    || opts.session?.routing?.primaryTrack
    || 'permit';
  const trackSkill = ({
    planning: 'if-intake-planning',
    license: 'if-intake-license',
    permit: 'if-intake-permit',
  })[String(track).toLowerCase()] || 'if-application-intake';
  const clawSkill = opts.session?.intakeSchema?.skillId || trackSkill;

  const staffSessionKey = opts.pkg?.intake?.staffSessionKey
    || opts.pkg?.staffSessionKey
    || `if-intake-staff:${jurisdictionId}`;

  def.metadata.interviewFlow = {
    ...(def.metadata.interviewFlow || {}),
    playbook: 'permits-intake',
    jurisdictionId,
    citizenMode: true,
    // Public path uses advise-intake HTTP; server runs live agent tool loop when enabled.
    publicAdvise: true,
    liveSkillLoop: true,
    boundFlowName: 'IF-citizen-thin-intake-v1',
    staffSessionKey,
    initialFormPrefill: defaults,
    intakeGates: {
      requireAddress: false,
      requirePropertyType: false,
      minAddressLength: 0,
      ...(def.metadata.interviewFlow?.intakeGates || {}),
    },
    claw: {
      skill: clawSkill,
      requirementsSkill: 'if-intake-requirements',
      tools: [
        'if_resolve_intake_requirements',
        'if_advise_application_intake',
        'if_ingest_intake_attachment',
        'if_extract_intake_from_document',
        'if_review_site_plan',
        'if_query_agency_policy',
        'if_export_panel_json',
      ],
      documentsSkill: 'if-intake-documents',
      demonstrates: ['skills', 'capabilities', 'agent-tool-loop', 'grounded-policy'],
    },
  };

  const left = (def.panels || []).find((p) => p.id === 'left_intake');
  if (left?.config?.formFields) {
    left.config.formFields = left.config.formFields.map((f) => {
      const key = f.name || f.id;
      if (defaults[key] == null || defaults[key] === '') return f;
      return { ...f, defaultValue: defaults[key], value: defaults[key] };
    });
    left.config.initialValues = defaults;
  }

  return def;
}

export const intakePublicDemoDefinition = buildIntakePublicDemoDefinition();

export async function loadIntakePublicDemoDefinition(opts = {}) {
  return buildIntakePublicDemoDefinition(opts);
}
